import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ControlsCommonProps } from '../../fields';
import styles from './multiSelect.module.scss';
import { Cover } from 'components/Cover';
import { PortalToDropdown } from "components/Portal/PortalToDropdown"
import { emptyArray, EntityPrefixEnum, getFormatedId } from 'utils/commonHelper';
import { GenericFilterModel, GenericFilterModelCollection, IdNameResponse, IdNameResponseItemsResponseModel } from 'services/tenantManagementService';
import { emptyItemsModel, ITEMS_LIMIT_MULTISELECT, ItemsModel } from '../AutoComplete/remoteHelper';
import { multiselectId } from './MultiSelect';
import { tryCatchJsonByAction } from 'utils/fetchUtils';
import { areNumberArraysSame } from 'utils/arrayUtil';
import { useControlContent } from './hooks/useControlContent';
import { Dropdown } from './Dropdown/Dropdown';
import { useInteractionObserver } from './hooks/useIntersectionObserver';

const getItemId = (item: IdNameResponse) => item.id;

export type MultiSelectRemoteProps = ControlsCommonProps<number[] | undefined> & {
	entityPrefix?: EntityPrefixEnum
	filters?: GenericFilterModel[]
	fetchItemsFunction(genericFilter: GenericFilterModelCollection, searchValue: string | undefined): Promise<IdNameResponseItemsResponseModel>
	fetchSelectedItemsFunction(ids: number[]): Promise<IdNameResponseItemsResponseModel>
	getItemText?(item: IdNameResponse): string
	numOfItemNames?: number
	isLink?: boolean
	getItemLinkRoute?(item: IdNameResponse): string
	size?: 'medium' | 'small'
}

export const MultiSelectRemote = (props: MultiSelectRemoteProps) => {
	const {
		value, onChange, onBlur, disabled,
		entityPrefix, filters, fetchItemsFunction, fetchSelectedItemsFunction, getItemText,
		getItemLinkRoute, isLink, numOfItemNames = 1, size = 'medium'
	} = props;

	const containerRef = useRef<HTMLDivElement>(null);
	const loadMoreRef = useRef(null);
	const [expanded, setExpanded] = useState(false);
	const [filterValue, setFilterValue] = useState<string>();
	const [filteredItems, setFilteredItems] = useState<ItemsModel>(emptyItemsModel);
	const [selectedItems, setSelectedItems] = useState<IdNameResponse[]>(emptyArray);
	const [fetchingInitialItems, setFetchingInitialItems] = useState(false);

	const notSelectedItems = filteredItems.items.filter((item) => !selectedItems.find((selectedItem) => item.id === selectedItem.id));

	const fetchFilteredItemsCallback = useCallback(
		async (newFilterValue: string | undefined, currentOffset: number) => {
			const model = new GenericFilterModelCollection({
				filters,
				offset: currentOffset,
				limit: ITEMS_LIMIT_MULTISELECT
			});
			const bindedAction = fetchItemsFunction.bind(null, model, newFilterValue);
			const response = await tryCatchJsonByAction(bindedAction);
			if (response.success) {
				let items = response.items || emptyArray;

				setFilteredItems((state => {
					return {
						items: currentOffset > 0 ? [...state.items, ...items] : items,
						count: response.count
					}
				}))
			}
		},
		[filters, fetchItemsFunction]
	)

	const filterItemsCallback = useCallback(
		async (newFilterValue: string | undefined) => {
			setFilterValue(newFilterValue);
			await fetchFilteredItemsCallback(newFilterValue, 0);
		},
		[fetchFilteredItemsCallback]
	)

	const fetchSelectedItemsCallback = useCallback(
		async (ids: number[]) => {
			const bindedAction = fetchSelectedItemsFunction.bind(null, ids);
			const response = await tryCatchJsonByAction(bindedAction);
			if (response.success) {
				let newItems = response.items || emptyArray;

				// some item may be deleted from database but still selected (its id is in value array), so in that case we are adding "empty" item
				// (current example is Rules And Alerts where we have selected tickets that don't exist anymore)
				if (newItems.length !== ids.length) {
					newItems = ids.map((id) => {
						const newItem = newItems.find((item) => item.id === id);
						return newItem || new IdNameResponse({ id, name: '' })
					})
				}
				setSelectedItems(newItems);
			}
		},
		[fetchSelectedItemsFunction]
	)

	useEffect(
		() => {
			const valueIds = value || emptyArray;
			const selectedItemsIds = selectedItems.map((item) => getItemId(item));

			const areSame = areNumberArraysSame(valueIds, selectedItemsIds);
			if (!areSame) {
				fetchSelectedItemsCallback(valueIds);
			}
		},
		[fetchSelectedItemsCallback, value, selectedItems]
	)

	useInteractionObserver({
		filteredItems,
		fetchFilteredItemsCallback,
		filterValue,
		loadMoreRef
	});

	const onChangeCallback = useCallback(
		(newSelectedItems: any[]) => {
			const ids = newSelectedItems.map(item => getItemId(item));
			onChange && onChange(ids);
			setSelectedItems(newSelectedItems);
		},
		[onChange]
	)

	const onBlurCallback = useCallback(
		() => {
			setExpanded(false);
			setFilterValue(undefined);
			setFilteredItems(emptyItemsModel);
			onBlur && onBlur();
		},
		[onBlur]
	)

	const getItemTextMemo = useMemo(
		() => {
			// if getItemText is provided use it
			if (getItemText) {
				return getItemText;
			}

			// if entity prefix is provided, create default getItemText
			if (entityPrefix) {
				return (item: IdNameResponse) => `${getFormatedId(entityPrefix, item.id)} - ${item.name}`;
			}

			// otherwise, return just name
			return (item: IdNameResponse) => item.name;
		},
		[entityPrefix, getItemText]
	)

	const controlContent = useControlContent({
		selectedItems,
		getItemId,
		getItemText: getItemTextMemo,
		getItemLinkRoute,
		isLink,
		count: filteredItems.count || 0,
		numOfItemNames,
		expanded,
		size,
		disabled
	})

	const expandCallback = useCallback(
		async () => {
			if (expanded || (disabled && selectedItems.length === 0)) {
				// prevent fetch filtered items to trigger when we don't want
				return;
			}
			setExpanded(true);
			if (!disabled) {
				setFetchingInitialItems(true);
				await fetchFilteredItemsCallback(undefined, 0);
				setFetchingInitialItems(false);
			}
		},
		[expanded, disabled, selectedItems.length, fetchFilteredItemsCallback]
	)

	return (
		<div ref={containerRef} id={multiselectId} className={`${styles.container} ${size === 'small' ? styles.small : ''}`}>
			{expanded && <Cover onClick={onBlurCallback} transparent />}
			<div className={styles.select_container} onClick={expandCallback}>
				{/* control */}
				{controlContent}
				{/* dropdown */}
				{expanded &&
					<PortalToDropdown containerRef={containerRef}>
						<Dropdown
							ref={loadMoreRef}
							selectedItems={selectedItems}
							filteredItems={notSelectedItems}
							filterItems={filterItemsCallback}
							getItemId={getItemId}
							getItemText={getItemTextMemo}
							fetchingInitialItems={fetchingInitialItems}
							hasMoreItems={!disabled && filteredItems.count !== undefined && filteredItems.items.length < filteredItems.count}
							onChange={onChangeCallback}
							disabled={!!disabled}
							useDebounce
						/>
					</PortalToDropdown>
				}
			</div>
		</div>
	)
}
